import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import fetchAcceptCSTChallenge, {
  AcceptCSTChallengeData,
} from 'features/auth/components/CstLinkToken/utils/fetchAcceptCSTChallenge';
import { useAuthUrl } from 'features/auth/hooks/useAuthUrl/useAuthUrl';
import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { FeatureFlagKey } from 'services/statsig/types';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

const ERROR_SCREEN_TYPE = 'LINK_EXPIRED';

const CstLinkToken = () => {
  const router = useRouter();
  const { tokenRef } = useLocalSearchParams<{ tokenRef: string }>();
  const pathname = usePathname();
  const cstLinkToken = tokenRef as string;
  const [isLoading, setIsLoading] = useState(false);
  const hasProcessedToken = useRef(false);
  const { getVaroSessionId, idToken, isLoggedIn } =
    useContext<AuthContextProps>(AuthContext);
  const authUrl = useAuthUrl(
    FeatureFlagKey.ACCEPT_CHALLENGE_USE_K8S_AUTH_SERVICE,
  );

  useLayoutEffect(() => {
    const confirmToken = async () => {
      try {
        setIsLoading(true);

        const data: AcceptCSTChallengeData = {
          challengeId: '',
          otpCode: '',
          acceptOptions: {
            tokenOption: {
              tokenReference: cstLinkToken,
            },
          },
        };

        let headers;
        if (isLoggedIn && idToken) {
          const varoSessionId = await getVaroSessionId();
          headers = await getLambdaHeaders(idToken, varoSessionId);
        } else {
          headers = await getLambdaHeaders();
        }

        const { challengeVerified = null } = await fetchAcceptCSTChallenge(
          authUrl,
          data,
          headers,
        );

        setIsLoading(false);

        if (challengeVerified === true) {
          router.replace({
            pathname: pathname.includes('cst-token-sms')
              ? '/challenge/cst-token-sms/verify-success'
              : '/challenge/cst-link/verify-success',
          } as any);
        } else {
          router.replace({
            pathname: pathname.includes('cst-token-sms')
              ? '/challenge/cst-token-sms/verify-failure'
              : '/challenge/cst-link/verify-failure',
            params: {
              screenType: ERROR_SCREEN_TYPE,
            },
          } as any);
        }
      } catch (error) {
        setIsLoading(false);
        logErrorWithContext(
          'AcceptChallenge error in cst link token: ' + error,
          {
            featureArea: FeatureArea.AUTHENTICATION,
            responsibleTeam: ResponsibleTeam.FRAUD,
            severity: ErrorSeverity.CRITICAL,
          },
        );

        router.replace({
          pathname: pathname.includes('cst-token-sms')
            ? '/challenge/cst-token-sms/verify-failure'
            : '/challenge/cst-link/verify-failure',
          params: {
            screenType: ERROR_SCREEN_TYPE,
          },
        } as any);
      }
    };

    if (cstLinkToken && !hasProcessedToken.current) {
      hasProcessedToken.current = true;
      void confirmToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cstLinkToken, router]);

  return (
    <View style={styles.container}>
      <ScreenTransitionOverlay
        isLoading={isLoading}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default CstLinkToken;
